import React from "react";

import {
  getSingleAssetBindingView,
  getSingleAssetMarketView,
} from "../../api/client";
import {
  INVESTMENT_AGENT_PANEL_WIDTH_KEY,
  useAgentPanelLayout,
} from "../../hooks/useAgentPanelLayout";
import { SingleAssetAgentDev } from "../single-asset-agent-dev/SingleAssetAgentDev";
import { AGENT_PANEL_ENABLED } from "../../agentPanel";
import styles from "./overviewAgentPanel.module.css";

// 概览 / 标签汇总页共用的左侧 Agent 对话栏（原型 v23：这几页版式一致，左边都是 337px 的投资理财助手）。
// 返回 style（给页面根节点，带 --overview-agent-width）、panel（<aside>）、agentHandoff（当前对话，进持仓页时带过去）。
// Agent 给出结果后的跳转（进行情 / 持仓 / 工作台）也在这里，各页行为一致。
export function useOverviewAgent({ navigate, onLogout, initialAgentHandoff = null, onError }) {
  const [agentHandoff, setAgentHandoff] = React.useState(initialAgentHandoff);
  const { agentPanelWidth, startAgentPanelResize } = useAgentPanelLayout({
    isDashboard: false,
    initialWidth: 420,
    centerMinWidth: 900,
    rightRailWidth: 0,
    storageKey: INVESTMENT_AGENT_PANEL_WIDTH_KEY,
  });

    async function openAgentResult(handoff) {
      const result = handoff?.turnResult ?? {};
      const binding = result.binding;
      // 在对话里只打了一个标的名时没有绑定——/parse 不写库。照 openWatch 那条路把
      // 行情交给完整工作台：URL 上的 ?asset= 正是 useSingleAssetMarketView 的第一条
      // 加载路径，路由态里再带一份现成的行情，落地就不用再取一次。
      if (!binding?.binding_id && result.market_view?.asset_code) {
        const query = new URLSearchParams({ asset: result.market_view.asset_code });
        if (result.conversation_id) query.set("conversation", result.conversation_id);
        navigate(`/single?${query.toString()}`, {
          state: {
            singleAssetAgentHandoff: handoff,
            singleAssetMarketView: result.market_view,
          },
        });
        return;
      }
      if (!result.conversation_id || !binding?.binding_id) {
        onError("Agent 已返回结果，但尚未建立可恢复的页面目标。请继续在对话中补充。 ");
        return;
      }
      try {
        const query = new URLSearchParams({
          conversation: result.conversation_id,
          binding: binding.binding_id,
        });
        const routeState = { singleAssetAgentHandoff: handoff };
        if (binding.view_type === "market") {
          const code = result.market_view?.asset_code || binding.asset_code;
          if (!code) return;
          query.set("asset", code);
          routeState.singleAssetMarketView = result.market_view
            || await getSingleAssetMarketView(code);
        } else if (binding.target_type === "holding" && binding.target_id) {
          query.set("holding", binding.target_id);
          routeState.singleAssetBindingView = await getSingleAssetBindingView(
            result.conversation_id,
            binding.binding_id,
          );
        } else if (binding.target_type === "workspace" && binding.target_id) {
          query.set("workspace", binding.target_id);
          routeState.singleAssetBindingView = await getSingleAssetBindingView(
            result.conversation_id,
            binding.binding_id,
          );
        } else {
          // create_on_write 仍在等待用户补全时没有可展示的核算对象；问题只留
          // 在 Agent 中，右侧继续保留概览，不进入旧问题页或空白页。
          return;
        }
        navigate(`/single?${query.toString()}`, { state: routeState });
      } catch (requestError) {
        onError(requestError.message || "暂时无法打开 Agent 结果。 ");
      }
    }

  const panel = (
    <aside className={`${styles.agentPanel} resizable-agent-panel`}>
      <div
        className="agent-resize-handle"
        role="separator"
        aria-orientation="vertical"
        aria-label="拖动调整 Agent 宽度"
        onMouseDown={startAgentPanelResize}
      />
      <SingleAssetAgentDev
        embedded
        initialAgentHandoff={initialAgentHandoff}
        onAgentStateChange={setAgentHandoff}
        onLogout={onLogout}
        onResultOpen={(handoff) => { void openAgentResult(handoff); }}
        onScreenerOpen={(agentQuery) => navigate("/screener", { state: { agentQuery } })}
      />
    </aside>
  );
  // 对话框收起时页面只剩一列（行内样式压过各页 CSS 里预留对话栏的那一列，含媒体查询）
  return AGENT_PANEL_ENABLED
    ? { style: { "--overview-agent-width": `${agentPanelWidth}px` }, panel, agentHandoff }
    : { style: { gridTemplateColumns: "minmax(0, 1fr)" }, panel: null, agentHandoff };
}
